// AstriaBrazilV2Shell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Heart, Hourglass, CloudSun, Compass, NotebookPen } from "lucide-react";
import { BrazilV2Lang } from "./brazilV2Language";
import { BRAZIL_V2_COPY } from "./brazilV2Copy";

// ─── Astria Brazil v2 — shared shell (header + sticky tab bar) ────────────
// Per client spec (BrMaxico.txt — BR v2 Full Specification): warm,
// expressive, open, supportive, light-reflective. Medium intensity (higher
// than PH, lower than IN). Non-formal, non-poetic, non-spiritual. Palette
// taken verbatim from the client's "BR Lane v2 — UI JSON Pack": warm yellow
// primary (#F4B740), soft orange secondary (#FF8A65), friendly green accent
// (#4CAF50) — distinct from VN v2's jade/lotus-pink, but same structural
// pattern (see AstriaVietnamV2Shell.tsx / AstriaPhilippinesV2Shell.tsx /
// AstriaIndonesiaV2Shell.tsx). 6 fixed tabs, no birth chart, no LLM — see
// helper/astriaBrazilV2Service.js on the backend.
//
// Bilingual (EN/PT) — a self-contained toggle in the header switches all UI
// chrome text via brazilV2Copy.ts. Scoped to this feature only; does not
// touch the app-wide i18next setup. See brazilV2Language.ts.

export const BRAZIL_V2_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const BRAZIL_V2_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const BRAZIL_V2_MAX_WIDTH = 960;

export const SHELL_COLORS_BRAZIL_V2 = {
  bg: "linear-gradient(160deg, #FFFDF8 0%, #FFF8E1 55%, #FFF3E0 100%)",
  card: "#FFFFFF",
  section: "#FFF3E0",
  cardBorder: "#FBE3B6",
  titleColor: "#2D2D2D",
  sectionTitleColor: "#2D2D2D",
  bodyColor: "#3D3D3D",
  mutedColor: "#5A5A5A",
  accent: "#F4B740",
  accentSoft: "rgba(244,183,64,0.16)",
  accentGlow: "0 0 0 3px rgba(244,183,64,0.18)",
  gold: "#FF8A65",
  goldSoft: "rgba(255,138,101,0.14)",
  green: "#4CAF50",
  white: "#FFFFFF",
};

export type AstriaBrazilV2Tab =
  | "soft_summary"
  | "connection_atmosphere"
  | "timing_rhythm"
  | "emotion_balance"
  | "guidance_path"
  | "your_note";

interface TabDef {
  key: AstriaBrazilV2Tab;
  // The SubCategory name exactly as seeded by
  // scripts/createAstriaBrazilV2Category.js — always English, used to
  // resolve categoryId/subCategoryId in useBrazilV2Tab.ts regardless of
  // the active display language. Never render this directly; use the
  // language-keyed copy.tabBar[key].label for display.
  dbName: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

// Icons/order per BrMaxico.txt's BR v2 UI pack: 🌙 💛 ⏳ 🌤️ 🧭 📝
const TABS: TabDef[] = [
  {
    key: "soft_summary",
    dbName: "Soft Summary",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-brazil-v2/soft-summary",
    accent: ["#F4B740", "#FF8A65"],
  },
  {
    key: "connection_atmosphere",
    dbName: "Connection Atmosphere",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-brazil-v2/connection-atmosphere",
    accent: ["#FF8A65", "#F4B740"],
  },
  {
    key: "timing_rhythm",
    dbName: "Timing Rhythm",
    icon: <Hourglass className="w-4 h-4" />,
    path: "/astria-brazil-v2/timing-rhythm",
    accent: ["#F4B740", "#4CAF50"],
  },
  {
    key: "emotion_balance",
    dbName: "Emotion Balance",
    icon: <CloudSun className="w-4 h-4" />,
    path: "/astria-brazil-v2/emotion-balance",
    accent: ["#4CAF50", "#F4B740"],
  },
  {
    key: "guidance_path",
    dbName: "Guidance Path",
    icon: <Compass className="w-4 h-4" />,
    path: "/astria-brazil-v2/guidance-path",
    accent: ["#F4B740", "#FF8A65"],
  },
  {
    key: "your_note",
    dbName: "Your Note",
    icon: <NotebookPen className="w-4 h-4" />,
    path: "/astria-brazil-v2/your-note",
    accent: ["#FF8A65", "#4CAF50"],
  },
];

interface AstriaBrazilV2ShellProps {
  active: AstriaBrazilV2Tab;
  lang: BrazilV2Lang;
  onLangChange: (lang: BrazilV2Lang) => void;
  children: React.ReactNode;
}

const AstriaBrazilV2Shell: React.FC<AstriaBrazilV2ShellProps> = ({
  active,
  lang,
  onLangChange,
  children,
}) => {
  const navigate = useNavigate();
  const copy = BRAZIL_V2_COPY[lang];

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_BRAZIL_V2.bg,
        fontFamily: BRAZIL_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,253,248,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_BRAZIL_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: BRAZIL_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_BRAZIL_V2.accent} 0%, ${SHELL_COLORS_BRAZIL_V2.gold} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${SHELL_COLORS_BRAZIL_V2.accentSoft}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, color: "#fff" }}>💛</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: SHELL_COLORS_BRAZIL_V2.titleColor,
                fontFamily: BRAZIL_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              {copy.shell.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: SHELL_COLORS_BRAZIL_V2.mutedColor,
              }}
            >
              {copy.shell.subtitle}
            </div>
          </div>

          {/* ─── PT / EN language toggle ─────────────────────────────── */}
          <div
            role="group"
            aria-label={copy.shell.langToggleLabel}
            style={{
              display: "flex",
              flexShrink: 0,
              borderRadius: 999,
              background: SHELL_COLORS_BRAZIL_V2.section,
              border: `1px solid ${SHELL_COLORS_BRAZIL_V2.cardBorder}`,
              padding: 3,
              gap: 2,
            }}
          >
            {(["pt", "en"] as BrazilV2Lang[]).map((code) => {
              const isActive = code === lang;
              return (
                <button
                  key={code}
                  onClick={() => onLangChange(code)}
                  aria-pressed={isActive}
                  className="rounded-full transition-all"
                  style={{
                    paddingInline: 10,
                    paddingBlock: 5,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.03em",
                    color: isActive ? "#2D2D2D" : SHELL_COLORS_BRAZIL_V2.mutedColor,
                    background: isActive
                      ? `linear-gradient(135deg, ${SHELL_COLORS_BRAZIL_V2.accent} 0%, ${SHELL_COLORS_BRAZIL_V2.gold} 100%)`
                      : "transparent",
                  }}
                >
                  {code.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: BRAZIL_V2_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: isActive
                      ? "#2D2D2D"
                      : SHELL_COLORS_BRAZIL_V2.bodyColor,
                    background: isActive
                      ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                      : SHELL_COLORS_BRAZIL_V2.section,
                    boxShadow: isActive
                      ? `0 4px 14px ${SHELL_COLORS_BRAZIL_V2.accentSoft}`
                      : "none",
                    border: isActive
                      ? "none"
                      : `1px solid ${SHELL_COLORS_BRAZIL_V2.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{copy.tabBar[tab.key].label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background:
                "linear-gradient(90deg, rgba(255,253,248,0) 0%, rgba(255,253,248,0.9) 100%)",
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: BRAZIL_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaBrazilV2Shell;
export { TABS as ASTRIA_BRAZIL_V2_TABS };
